import React, { useMemo, useState, useEffect, useRef } from "react";

import { Box, Grid, Tooltip } from "@mui/material/";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { SingleAcrissTablesInput } from "../../availability/service/availabilityReportInterfaces";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import dayjs from "dayjs";
import { formatNegativeNumber, formatPositiveNumber } from "../../../utils";
import { useTranslation } from "react-i18next";

const SingleAcrissTables: React.FC<SingleAcrissTablesInput> = ({
  singleAcrissRow,
  configurationThresholds,
  fullDays,
}) => {
  const leftGridRef = useRef<AgGridReact>(null);
  const rightGridRef = useRef<AgGridReact>(null);
  const { t } = useTranslation(["operation"]);
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
  const defaultColDef: ColDef = {
    suppressMovable: true,
  };
  const [columnDefs, setColumnDefs] = useState<any[]>([]);
  const [rowData, setRowData] = useState<any>({ leftSide: [], rightSide: [] });

  const isNullOrUndefined = (value: any) => {
    return typeof value === "undefined" || value === null;
  };

  const availabilityLow =
    configurationThresholds?.filter(
      (item) => item.thresholdName === "AVAILABILITY_MEDIUM"
    )[0].minValueIncluded || 0;

  const availabilityBelow0 =
    configurationThresholds?.filter(
      (item) => item.thresholdName === "AVAILABILITY_BELOW0"
    )[0].maxValueExcluded || 0;

  const availabilityHigh =
    configurationThresholds?.filter(
      (item) => item.thresholdName === "AVAILABILITY_MEDIUM"
    )[0].maxValueExcluded || 0;

  const utilizationLow =
    configurationThresholds?.filter(
      (item) => item.thresholdName === "UTILIZATION_LOW"
    )[0].maxValueExcluded || 0;

  const utilizationHigh =
    configurationThresholds?.filter(
      (item) => item.thresholdName === "UTILIZATION_GOOD"
    )[0].maxValueExcluded || 0;

  const utilizationLabel = t("availabilityReportForecast.utilization");

  const cellClassRules = {
    boldLabels: (params: { colDef: ColDef; data: any }) =>
      (params.colDef.field === "acrissLabel" && params.data.isAcriss) ||
      params.colDef.field === "valueType",
    whiteSpace: (params: { colDef: ColDef; data: any }) =>
      params.colDef.field === "valueType",
    red: (params: { data: any; value: any }) =>
      typeof params.value !== "string" &&
      params.data.valueType === utilizationLabel &&
      params.value >= utilizationHigh * 100,
    yellow: (params: { data: any; value: any }) =>
      typeof params.value !== "string" &&
      params.data.valueType === utilizationLabel &&
      params.value >= utilizationLow * 100 &&
      params.value < utilizationHigh * 100,
    green: (params: { data: any; value: any }) =>
      typeof params.value !== "string" &&
      params.data.valueType === utilizationLabel &&
      params.value < utilizationLow * 100,
    fontWhite: (params: { data: any; value: any }) =>
      typeof params.value !== "string" &&
      params.data.valueType === utilizationLabel,
    grey: (params: { data: any; value: any }) =>
      (typeof params.value === "string" &&
        params.data.valueType === utilizationLabel) ||
      (isNaN(params.value) && params.data.valueType === utilizationLabel),

    borderBottom: (params: { data: any }) =>
      params.data.valueType === utilizationLabel,
    borderRight: (params: { colDef: ColDef }) =>
      params.colDef.headerName === "",
  };

  const renderAssignmentToolptip = (
    startReservationNeed: number,
    startUnavailabilityNeed: number,
    sellOrderNeed: number,
    endReservationSupply: number,
    endUnavailabilitySupply: number,
    purchaseOrderSupply: number,
    value: number,
    isActualAssignment: boolean,
    arrowValues: any
  ) => {
    return (
      <div>
        {t("availabilityReport.tooltipTitles.reservationsStarted")}
        <span style={{ float: "right", marginLeft: "10px" }}>
          {startReservationNeed}
        </span>
        <br />
        {t("availabilityReport.tooltipTitles.unavailabiltiesStarted")}
        <span style={{ float: "right", marginLeft: "10px" }}>
          {startUnavailabilityNeed}
        </span>
        <br />
        {t("availabilityReport.tooltipTitles.reservationsEnded")}
        <span style={{ float: "right", marginLeft: "10px" }}>
          {endReservationSupply ? "-" : ""}
          {endReservationSupply}
        </span>
        <br />
        {t("availabilityReport.tooltipTitles.unavailabilitiesEnded")}
        <span style={{ float: "right", marginLeft: "10px" }}>
          {endUnavailabilitySupply ? "-" : ""}
          {endUnavailabilitySupply}
        </span>

        <br />
        {!isNaN(value) && (
          <div
            style={{
              backgroundColor: "rgb(255,255,255, 0.5)",
              marginTop: "10px",
              marginLeft: "-16px",
              marginRight: "-16px",
              paddingLeft: "16px",
              paddingRight: "16px",
            }}
          >
            {t("availabilityReport.tooltipTitles.total")}
            <span style={{ float: "right", marginLeft: "10px" }}>{value}</span>
          </div>
        )}

        <div>
          {!isNaN(value) ? (
            (arrowValues?.values?.down || arrowValues?.values?.up) &&
            isActualAssignment ? (
              <div>
                <br />
                {arrowValues?.values?.down && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    {arrowValues?.values?.downIcon}

                    <div style={{ width: "100%", marginLeft: "5px" }}>
                      {(arrowValues?.values?.down || []).map((item: any) => {
                        return (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>{item.title}:</span>
                            <span style={{ marginLeft: 2 }}>{item.value}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {arrowValues?.values?.up && (
                  <div
                    style={{
                      display: "flex",
                      marginTop: 20,
                    }}
                  >
                    {arrowValues?.values?.upIcon}

                    <div style={{ width: "100%", marginLeft: "5px" }}>
                      {(arrowValues?.values?.up || []).map((item: any) => {
                        return (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>{item.title}:</span>
                            <span style={{ marginLeft: 10 }}>{item.value}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              ``
            )
          ) : (
            <>
              <br />
              {t("availabilityReport.tooltipTitles.notAvailable")}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderAvailabilityTooltip = (
    assignation: number,
    stock: number,
    value: number
  ) => {
    return (
      <div>
        {t("availabilityReport.tooltipTitles.stock")}
        <span style={{ float: "right", marginLeft: "10px" }}>{stock}</span>
        <br />
        {t("availabilityReport.tooltipTitles.assignation")}
        <span style={{ float: "right", marginLeft: "10px" }}>
          {assignation}
        </span>

        <br />

        <div
          style={{
            backgroundColor: "rgb(255,255,255, 0.5)",
            marginTop: "10px",
            marginLeft: "-16px",
            marginRight: "-16px",
            paddingLeft: "16px",
            paddingRight: "16px",
          }}
        >
          {t("availabilityReport.tooltipTitles.total")}
          <span style={{ float: "right", marginLeft: "10px" }}>{value}</span>
        </div>
      </div>
    );
  };

  const renderUtilizationTooltip = (
    assignation: number,
    stock: number,
    value: number,
    arrowValues: any
  ) => {
    return (
      <div>
        {t("availabilityReport.tooltipTitles.assignation")}
        <span style={{ float: "right", marginLeft: "10px" }}>
          {assignation}
        </span>
        <br />
        {t("availabilityReport.tooltipTitles.stock")}
        <span style={{ float: "right", marginLeft: "10px" }}>{stock}</span>

        {!isNaN(value) && (
          <div
            style={{
              backgroundColor: "rgb(255,255,255, 0.5)",
              marginTop: "10px",
              marginLeft: "-16px",
              marginRight: "-16px",
              paddingLeft: "16px",
              paddingRight: "16px",
            }}
          >
            {t("availabilityReport.tooltipTitles.ratio")}
            <span
              style={{ float: "right", marginLeft: "10px" }}
            >{`${value}%`}</span>
          </div>
        )}

        <div>
          {!isNaN(value) ? (
            arrowValues?.values?.down || arrowValues?.values?.up ? (
              <div>
                <br />
                {arrowValues?.values?.down && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    {arrowValues?.values?.downIcon}

                    <div style={{ width: "100%", marginLeft: "5px" }}>
                      {(arrowValues?.values?.down || []).map((item: any) => {
                        return (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>{item.title}:</span>
                            <span style={{ marginLeft: 2 }}>{item.value}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {arrowValues?.values?.up && (
                  <div
                    style={{
                      display: "flex",
                      marginTop: 20,
                    }}
                  >
                    {arrowValues?.values?.upIcon}

                    <div style={{ width: "100%", marginLeft: "5px" }}>
                      {(arrowValues?.values?.up || []).map((item: any) => {
                        return (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>{item.title}:</span>
                            <span style={{ marginLeft: 2 }}>{item.value}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              ``
            )
          ) : (
            <>
              <br />
              {t("availabilityReport.tooltipTitles.notAvailable")}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderSign = (currentValue: number, prevValue: number) => {
    return currentValue - prevValue < 0
      ? "-"
      : currentValue - prevValue > 0
      ? "+"
      : "";
  };

  const handleRowAcriss = () => {
    const leftSideDateValues: any = {};
    let leftSideRows: any[] = [];
    const leftSideUtilization: any = {};
    const rightSideDateValues: any = {};
    let rightSideRows: any[] = [];
    const rightSideUtilization: any = {};

    // LEFT SIDE
    // assignation
    singleAcrissRow?.aggregatedRows.forEach((el) => {
      const currentDate = fullDays
        ? `${dayjs(el.previewDate).format("DD")}-${dayjs(el.previewDate).format(
            "MM"
          )}-${dayjs(el.previewDate).format("YY")}`
        : `${dayjs(el.previewDate).format("DD")}-${dayjs(el.previewDate).format(
            "MM"
          )}-${dayjs(el.previewDate).format("YY")}-${dayjs(
            el.previewDate
          ).format("A")}`;
      leftSideDateValues[currentDate] = el.assignation;
      leftSideDateValues[`${currentDate}-startReservationNeed`] =
        el.startReservationNeed;
      leftSideDateValues[`${currentDate}-startUnavailabilityNeed`] =
        el.startUnavailabilityNeed;
      leftSideDateValues[`${currentDate}-sellOrderNeed`] = el.sellOrderNeed;
      leftSideDateValues[`${currentDate}-endReservationSupply`] =
        el.endReservationSupply;
      leftSideDateValues[`${currentDate}-endUnavailabilitySupply`] =
        el.endUnavailabilitySupply;
      leftSideDateValues[`${currentDate}-purchaseOrderSupply`] =
        el.purchaseOrderSupply;

      leftSideDateValues[`${currentDate}-startingElsewhereReservationSupply`] =
        el.startingElsewhereReservationSupply;

      leftSideDateValues[`${currentDate}-endingElsewhereReservationNeed`] =
        el.endingElsewhereReservationNeed;

      leftSideDateValues[
        `${currentDate}-startingElsewhereUnavailabilitySupply`
      ] = el.startingElsewhereUnavailabilitySupply;

      leftSideDateValues[`${currentDate}-endingElsewhereUnavailabilityNeed`] =
        el.endingElsewhereUnavailabilityNeed;
    });

    leftSideRows.push({
      valueType: t("availabilityReportForecast.actualAssignment"),
      valueTypeTooltip: t("availabilityReportForecast.actualAssignment"),
      current: singleAcrissRow?.currentAssignment,
      acrissLabel: singleAcrissRow?.acriss,
      acrissTooltip: singleAcrissRow.acrissLabel,
      acrissCategory: singleAcrissRow?.acriss,
      isAcriss: true,
      ...leftSideDateValues,
    });

    singleAcrissRow?.singleSipps
      .filter((item) => !!item.sipp)
      .forEach((singleSipp) => {
        singleSipp.sippRows.forEach((el) => {
          const currentDate = fullDays
            ? `${dayjs(el.previewDate).format("DD")}-${dayjs(
                el.previewDate
              ).format("MM")}-${dayjs(el.previewDate).format("YY")}`
            : `${dayjs(el.previewDate).format("DD")}-${dayjs(
                el.previewDate
              ).format("MM")}-${dayjs(el.previewDate).format("YY")}-${dayjs(
                el.previewDate
              ).format("A")}`;
          leftSideDateValues[currentDate] = el.assignation;
          leftSideDateValues[`${currentDate}-startReservationNeed`] =
            el.startReservationNeed;
          leftSideDateValues[`${currentDate}-startUnavailabilityNeed`] =
            el.startUnavailabilityNeed;
          leftSideDateValues[`${currentDate}-sellOrderNeed`] = el.sellOrderNeed;
          leftSideDateValues[`${currentDate}-endReservationSupply`] =
            el.endReservationSupply;
          leftSideDateValues[`${currentDate}-endUnavailabilitySupply`] =
            el.endUnavailabilitySupply;
          leftSideDateValues[`${currentDate}-purchaseOrderSupply`] =
            el.purchaseOrderSupply;

          leftSideDateValues[
            `${currentDate}-startingElsewhereReservationSupply`
          ] = el.startingElsewhereReservationSupply;

          leftSideDateValues[`${currentDate}-endingElsewhereReservationNeed`] =
            el.endingElsewhereReservationNeed;

          leftSideDateValues[
            `${currentDate}-startingElsewhereUnavailabilitySupply`
          ] = el.startingElsewhereUnavailabilitySupply;

          leftSideDateValues[
            `${currentDate}-endingElsewhereUnavailabilityNeed`
          ] = el.endingElsewhereUnavailabilityNeed;
        });
        leftSideRows.push({
          valueType: "",
          valueTypeTooltip: t("availabilityReportForecast.actualAssignment"),
          acrissLabel: singleSipp.sipp,
          acrissTooltip: singleSipp.sippLabel,
          acrissCategory: singleAcrissRow?.acriss,
          current: singleSipp.currentAssignment,
          isAcriss: false,
          ...leftSideDateValues,
        });
      });

    // availability
    singleAcrissRow?.aggregatedRows.forEach((el) => {
      const currentDate = fullDays
        ? `${dayjs(el.previewDate).format("DD")}-${dayjs(el.previewDate).format(
            "MM"
          )}-${dayjs(el.previewDate).format("YY")}`
        : `${dayjs(el.previewDate).format("DD")}-${dayjs(el.previewDate).format(
            "MM"
          )}-${dayjs(el.previewDate).format("YY")}-${dayjs(
            el.previewDate
          ).format("A")}`;
      leftSideDateValues[currentDate] = el.availability;
      leftSideDateValues[`${currentDate}-assignation`] = el.assignation;
      leftSideDateValues[`${currentDate}-stock`] = el.stock;
    });

    leftSideRows.push({
      valueType: t("availabilityReportForecast.actualAvailability"),
      valueTypeTooltip: t("availabilityReportForecast.actualAvailability"),
      acrissLabel: singleAcrissRow?.acriss,
      acrissTooltip: singleAcrissRow.acrissLabel,
      acrissCategory: singleAcrissRow?.acriss,
      current: singleAcrissRow.currentAvailability,
      isAcriss: true,
      ...leftSideDateValues,
    });

    singleAcrissRow?.singleSipps
      .filter((item) => !!item.sipp)
      .forEach((singleSipp) => {
        singleSipp.sippRows.forEach((el) => {
          const currentDate = fullDays
            ? `${dayjs(el.previewDate).format("DD")}-${dayjs(
                el.previewDate
              ).format("MM")}-${dayjs(el.previewDate).format("YY")}`
            : `${dayjs(el.previewDate).format("DD")}-${dayjs(
                el.previewDate
              ).format("MM")}-${dayjs(el.previewDate).format("YY")}-${dayjs(
                el.previewDate
              ).format("A")}`;
          leftSideDateValues[currentDate] = el.availability;
          leftSideDateValues[`${currentDate}-assignation`] = el.assignation;
          leftSideDateValues[`${currentDate}-stock`] = el.stock;
        });
        leftSideRows.push({
          valueType: "",
          valueTypeTooltip: t("availabilityReportForecast.actualAvailability"),
          acrissLabel: singleSipp.sipp,
          acrissTooltip: singleSipp.sippLabel,
          acrissCategory: singleAcrissRow?.acriss,
          current: singleSipp.currentAvailability,
          isAcriss: false,
          ...leftSideDateValues,
        });
      });

    //utilization
    singleAcrissRow?.aggregatedRows.forEach((el) => {
      const currentDate = fullDays
        ? `${dayjs(el.previewDate).format("DD")}-${dayjs(el.previewDate).format(
            "MM"
          )}-${dayjs(el.previewDate).format("YY")}`
        : `${dayjs(el.previewDate).format("DD")}-${dayjs(el.previewDate).format(
            "MM"
          )}-${dayjs(el.previewDate).format("YY")}-${dayjs(
            el.previewDate
          ).format("A")}`;
      leftSideUtilization[currentDate] = +(el.utilization * 100).toFixed(0);
      leftSideUtilization[`${currentDate}-assignation`] = el.assignation;
      leftSideUtilization[`${currentDate}-stock`] = el.stock;
    });

    leftSideRows.push({
      valueType: utilizationLabel,
      valueTypeTooltip: utilizationLabel,
      acrissLabel: "",
      acrissCategory: singleAcrissRow?.acriss,
      current: isNullOrUndefined(singleAcrissRow.currentUtilization)
        ? +((singleAcrissRow.currentUtilization as number) * 100).toFixed(0)
        : 0,
      isAcriss: false,
      ...leftSideUtilization,
    });

    // RIGHT SIDE
    // assignationForecast
    singleAcrissRow?.aggregatedRows.forEach((el) => {
      const currentDate = fullDays
        ? `${dayjs(el.previewDate).format("DD")}-${dayjs(el.previewDate).format(
            "MM"
          )}-${dayjs(el.previewDate).format("YY")}`
        : `${dayjs(el.previewDate).format("DD")}-${dayjs(el.previewDate).format(
            "MM"
          )}-${dayjs(el.previewDate).format("YY")}-${dayjs(
            el.previewDate
          ).format("A")}`;
      rightSideDateValues[currentDate] = el.assignationForecast;
    });

    rightSideRows.push({
      valueType: t("availabilityReportForecast.theoreticalAssignment"),
      valueTypeTooltip: t("availabilityReportForecast.theoreticalAssignment"),
      acrissLabel: singleAcrissRow?.acriss,
      current: singleAcrissRow?.currentAssignment,
      acrissTooltip: singleAcrissRow.acrissLabel,
      acrissCategory: singleAcrissRow?.acriss,
      isAcriss: true,
      ...rightSideDateValues,
    });

    singleAcrissRow?.singleSipps
      .filter((item) => !!item.sipp)
      .forEach((singleSipp) => {
        singleSipp.sippRows.forEach((el) => {
          const currentDate = fullDays
            ? `${dayjs(el.previewDate).format("DD")}-${dayjs(
                el.previewDate
              ).format("MM")}-${dayjs(el.previewDate).format("YY")}`
            : `${dayjs(el.previewDate).format("DD")}-${dayjs(
                el.previewDate
              ).format("MM")}-${dayjs(el.previewDate).format("YY")}-${dayjs(
                el.previewDate
              ).format("A")}`;
          rightSideDateValues[currentDate] = el.assignationForecast;
        });
        rightSideRows.push({
          valueType: "",
          acrissLabel: singleSipp.sipp,
          current: singleSipp.currentAssignment,
          acrissTooltip: singleSipp.sippLabel,
          acrissCategory: singleAcrissRow?.acriss,
          isAcriss: false,
          ...rightSideDateValues,
        });
      });

    // availabilityForecast
    singleAcrissRow?.aggregatedRows.forEach((el) => {
      const currentDate = fullDays
        ? `${dayjs(el.previewDate).format("DD")}-${dayjs(el.previewDate).format(
            "MM"
          )}-${dayjs(el.previewDate).format("YY")}`
        : `${dayjs(el.previewDate).format("DD")}-${dayjs(el.previewDate).format(
            "MM"
          )}-${dayjs(el.previewDate).format("YY")}-${dayjs(
            el.previewDate
          ).format("A")}`;
      rightSideDateValues[currentDate] = el.availabilityForecast;
    });

    rightSideRows.push({
      valueType: t("availabilityReportForecast.theoreticalAvailability"),
      valueTypeTooltip: t("availabilityReportForecast.theoreticalAvailability"),
      acrissLabel: singleAcrissRow?.acriss,
      current: singleAcrissRow.currentAvailability,
      acrissTooltip: singleAcrissRow.acrissLabel,
      acrissCategory: singleAcrissRow?.acriss,
      isAcriss: true,
      ...rightSideDateValues,
    });

    singleAcrissRow?.singleSipps
      .filter((item) => !!item.sipp)
      .forEach((singleSipp) => {
        singleSipp.sippRows.forEach((el) => {
          const currentDate = fullDays
            ? `${dayjs(el.previewDate).format("DD")}-${dayjs(
                el.previewDate
              ).format("MM")}-${dayjs(el.previewDate).format("YY")}`
            : `${dayjs(el.previewDate).format("DD")}-${dayjs(
                el.previewDate
              ).format("MM")}-${dayjs(el.previewDate).format("YY")}-${dayjs(
                el.previewDate
              ).format("A")}`;
          rightSideDateValues[currentDate] = el.availabilityForecast;
        });
        rightSideRows.push({
          valueType: "",
          valueTypeTooltip: t(
            "availabilityReportForecast.theoreticalAvailability"
          ),
          current: singleSipp.currentAvailability,
          acrissLabel: singleSipp.sipp,
          acrissTooltip: singleSipp.sippLabel,
          acrissCategory: singleAcrissRow?.acriss,
          isAcriss: false,
          ...rightSideDateValues,
        });
      });

    //utilization
    singleAcrissRow?.aggregatedRows.forEach((el) => {
      const currentDate = fullDays
        ? `${dayjs(el.previewDate).format("DD")}-${dayjs(el.previewDate).format(
            "MM"
          )}-${dayjs(el.previewDate).format("YY")}`
        : `${dayjs(el.previewDate).format("DD")}-${dayjs(el.previewDate).format(
            "MM"
          )}-${dayjs(el.previewDate).format("YY")}-${dayjs(
            el.previewDate
          ).format("A")}`;
      rightSideUtilization[currentDate] = +(
        el.utilizationForecast * 100
      ).toFixed(0);
    });

    rightSideRows.push({
      valueType: utilizationLabel,
      valueTypeTooltip: utilizationLabel,
      acrissLabel: "",
      acrissCategory: singleAcrissRow?.acriss,
      current: !isNullOrUndefined(singleAcrissRow.currentUtilization)
        ? +((singleAcrissRow.currentUtilization as number) * 100).toFixed(0)
        : NaN,
      isAcriss: false,
      ...rightSideUtilization,
    });

    setRowData({
      leftSide: leftSideRows,
      rightSide: rightSideRows,
    });
  };

  useEffect(() => {
    handleRowAcriss();
  }, []);

  const renderAcrissLabel = (params: ICellRendererParams) => {
    return params.value !== utilizationLabel ? (
      <Tooltip arrow={true} title={params.data.acrissTooltip}>
        <span>{params.value}</span>
      </Tooltip>
    ) : (
      <span>{params.value}</span>
    );
  };

  const timezoneOffset = useMemo(() => {
    return -1 * (new Date().getTimezoneOffset() / 60);
  }, []);

  useEffect(() => {
    setColumnDefs([
      {
        field: "tableName",
        headerName: singleAcrissRow?.acriss,
        children: [
          {
            field: "valueType",
            headerName: "",
            pinned: "left",
            width: 130,
            cellClassRules: cellClassRules,
          },
          {
            field: "acrissLabel",
            headerName: "",
            width: 100,
            pinned: "left",
            cellClassRules: cellClassRules,
            headerClass: "borderRight",
            cellRenderer: (params: ICellRendererParams) =>
              renderAcrissLabel(params),
          },
          {
            field: "current",
            headerName: t("availabilityReport.current"),
            width: 100,
            cellClassRules: cellClassRules,
            cellRenderer: (params: ICellRendererParams) => {
              const circleColor = () => {
                const { value } = params;

                if (value < availabilityBelow0) {
                  return "grey";
                }

                if (value < availabilityLow) {
                  return "red";
                }

                if (value >= availabilityHigh) {
                  return "green";
                }

                return "yellow";
              };
              return (
                <span style={{ width: "100%" }}>
                  {!isNaN(params.value)
                    ? `${params.value}`
                    : t(
                        "availabilityReport.tooltipTitles.notAvailableAchronym"
                      )}

                  {!isNaN(params.value) &&
                    params.data.valueType === utilizationLabel &&
                    "%"}
                  {(params.data.valueTypeTooltip ===
                    t("availabilityReportForecast.actualAvailability") ||
                    params.data.valueTypeTooltip ===
                      t(
                        "availabilityReportForecast.theoreticalAvailability"
                      )) && <span className={`circle ${circleColor()}`}></span>}
                </span>
              );
            },
          },
          ...singleAcrissRow?.aggregatedRows?.map((item: any) => ({
            field: fullDays
              ? `${dayjs(item.previewDate).format("DD")}-${dayjs(
                  item.previewDate
                ).format("MM")}-${dayjs(item.previewDate).format("YY")}`
              : `${dayjs(item.previewDate).format("DD")}-${dayjs(
                  item.previewDate
                ).format("MM")}-${dayjs(item.previewDate).format("YY")}-${dayjs(
                  item.previewDate
                ).format("A")}`,
            headerName: fullDays
              ? `${dayjs(item.previewDate).format("DD/MM/YYYY")}`
              : `${dayjs(item.previewDate).format("DD/MM/YYYY")} ${dayjs(item.previewDate).format("A") === "AM" ? "🌑" : "🔅"}`,
            headerTooltip: fullDays ? `
              ${dayjs().startOf("day").add(timezoneOffset, "hour").format("HH:mm")}
              -
              ${dayjs().startOf("day").add(24, "hour").add(timezoneOffset, "hour").add(-1, "minute").format("HH:mm")}` 
              : 
              `
              ${dayjs(item.previewDate).format("A") === "AM" ? (
                `${dayjs().startOf("day").add(timezoneOffset, "hour").format("HH:mm")}
                -
                ${dayjs().startOf("day").add(12, "hour").add(timezoneOffset, "hour").add(-1, "minute").format("HH:mm")}`
              ) : (
                `${dayjs().startOf("day").add(12, "hour").add(timezoneOffset, "hour").format("HH:mm")}
                - 
                ${dayjs().startOf("day").add(timezoneOffset, "hour").add(-1, "minute").format("HH:mm")}`
              )}
            `,
            width: 130,
            cellClassRules: cellClassRules,
            cellRenderer: (params: ICellRendererParams) => {
              const currentColId = params.column?.getColId() || 0;

              const circleColor = () => {
                const { value } = params;

                if (value < availabilityBelow0) {
                  return "grey";
                }

                if (value < availabilityLow) {
                  return "red";
                }

                if (value >= availabilityHigh) {
                  return "green";
                }

                return "yellow";
              };

              const arrowDirection = (isTooltip: boolean) => {
                const isUtilization =
                  params.data.valueType === utilizationLabel || isTooltip;
                let startingElsewhereReservationSupply = null;
                let endingElsewhereReservationNeed = null;
                let startingElsewhereUnavailabilitySupply = null;
                let endingElsewhereUnavailabilityNeed = null;

                const isActualAssignment =
                  params.data.valueType === "Actual assignment";

                if (isActualAssignment) {
                  startingElsewhereReservationSupply =
                    params.data[
                      `${params.colDef?.field}-startingElsewhereReservationSupply`
                    ];

                  endingElsewhereReservationNeed =
                    params.data[
                      `${params.colDef?.field}-endingElsewhereReservationNeed`
                    ];

                  startingElsewhereUnavailabilitySupply =
                    params.data[
                      `${params.colDef?.field}-startingElsewhereUnavailabilitySupply`
                    ];

                  endingElsewhereUnavailabilityNeed =
                    params.data[
                      `${params.colDef?.field}-endingElsewhereUnavailabilityNeed`
                    ];
                } else {
                  startingElsewhereReservationSupply =
                    item.startingElsewhereReservationSupply;
                  endingElsewhereReservationNeed =
                    item.endingElsewhereReservationNeed;
                  startingElsewhereUnavailabilitySupply =
                    item.startingElsewhereUnavailabilitySupply;
                  endingElsewhereUnavailabilityNeed =
                    item.endingElsewhereUnavailabilityNeed;
                }

                if (
                  (startingElsewhereReservationSupply > 0 ||
                    startingElsewhereUnavailabilitySupply > 0) &&
                  (endingElsewhereReservationNeed > 0 ||
                    endingElsewhereUnavailabilityNeed > 0)
                ) {
                  return {
                    values: {
                      down: [
                        {
                          title: t(
                            "availabilityReport.tooltipTitles.startingElsewhereReservationSupply"
                          ),
                          value: startingElsewhereReservationSupply,
                        },

                        {
                          title: t(
                            "availabilityReport.tooltipTitles.startingElsewhereUnavailabilitySupply"
                          ),
                          value: startingElsewhereUnavailabilitySupply,
                        },
                      ],

                      downIcon: (
                        <ArrowDownwardIcon
                          fontSize={isUtilization ? "medium" : "small"}
                        />
                      ),

                      up: [
                        {
                          title: t(
                            "availabilityReport.tooltipTitles.endingElsewhereReservationNeed"
                          ),
                          value: endingElsewhereReservationNeed,
                        },

                        {
                          title: t(
                            "availabilityReport.tooltipTitles.endingElsewhereUnavailabilityNeed"
                          ),
                          value: endingElsewhereUnavailabilityNeed,
                        },
                      ],

                      upIcon: (
                        <ArrowUpwardIcon
                          fontSize={isUtilization ? "medium" : "small"}
                        />
                      ),
                    },

                    icon: (
                      <div style={{ display: "flex" }}>
                        <ArrowDownwardIcon
                          fontSize={isUtilization ? "medium" : "small"}
                        />
                        <ArrowUpwardIcon
                          fontSize={isUtilization ? "medium" : "small"}
                        />
                      </div>
                    ),
                  };
                }

                if (
                  startingElsewhereReservationSupply > 0 ||
                  startingElsewhereUnavailabilitySupply > 0
                ) {
                  return {
                    values: {
                      down: [
                        {
                          title: t(
                            "availabilityReport.tooltipTitles.startingElsewhereReservationSupply"
                          ),
                          value: startingElsewhereReservationSupply,
                        },

                        {
                          title: t(
                            "availabilityReport.tooltipTitles.startingElsewhereUnavailabilitySupply"
                          ),
                          value: startingElsewhereUnavailabilitySupply,
                        },
                      ],
                      downIcon: (
                        <ArrowDownwardIcon
                          fontSize={isUtilization ? "medium" : "small"}
                        />
                      ),
                    },
                    icon: (
                      <ArrowDownwardIcon
                        fontSize={isUtilization ? "medium" : "small"}
                      />
                    ),
                  };
                }

                if (
                  endingElsewhereReservationNeed > 0 ||
                  endingElsewhereUnavailabilityNeed > 0
                ) {
                  return {
                    values: {
                      down: [
                        {
                          title: t(
                            "availabilityReport.tooltipTitles.endingElsewhereReservationNeed"
                          ),
                          value: endingElsewhereReservationNeed,
                        },

                        {
                          title: t(
                            "availabilityReport.tooltipTitles.endingElsewhereUnavailabilityNeed"
                          ),
                          value: endingElsewhereUnavailabilityNeed,
                        },
                      ],
                      downIcon: (
                        <ArrowUpwardIcon
                          fontSize={isUtilization ? "medium" : "small"}
                        />
                      ),
                    },
                    icon: (
                      <ArrowUpwardIcon
                        fontSize={isUtilization ? "medium" : "small"}
                      />
                    ),
                  };
                }

                return null;
              };

              return (
                <Tooltip
                  arrow={true}
                  title={
                    params.data.valueTypeTooltip ===
                    t("availabilityReportForecast.actualAssignment")
                      ? renderAssignmentToolptip(
                          params.data[
                            `${params.colDef?.field}-startReservationNeed`
                          ],
                          params.data[
                            `${params.colDef?.field}-startUnavailabilityNeed`
                          ],
                          params.data[`${params.colDef?.field}-sellOrderNeed`],
                          params.data[
                            `${params.colDef?.field}-endReservationSupply`
                          ],
                          params.data[
                            `${params.colDef?.field}-endUnavailabilitySupply`
                          ],
                          params.data[
                            `${params.colDef?.field}-purchaseOrderSupply`
                          ],
                          params.value,
                          params.data.valueTypeTooltip === "Actual assignment",
                          arrowDirection(true)
                        )
                      : params.data.valueTypeTooltip ===
                        t("availabilityReportForecast.actualAvailability")
                      ? renderAvailabilityTooltip(
                          params.data[`${params.colDef?.field}-assignation`],
                          params.data[`${params.colDef?.field}-stock`],
                          params.value
                        )
                      : params.data.valueTypeTooltip === utilizationLabel
                      ? renderUtilizationTooltip(
                          params.data[`${params.colDef?.field}-assignation`],
                          params.data[`${params.colDef?.field}-stock`],
                          params.value,
                          arrowDirection(true)
                        )
                      : params.value
                  }
                >
                  <span
                    style={{
                      width: "100%",
                      ...((params.data.valueType === utilizationLabel ||
                        params.data.valueTypeTooltip ===
                          "Actual assignment") && {
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }),
                    }}
                  >
                    {isNaN(params.value)
                      ? t(
                          "availabilityReport.tooltipTitles.notAvailableAchronym"
                        )
                      : params.value >= 0
                      ? formatPositiveNumber(params.value)
                      : `-${formatNegativeNumber(params.value)}`}

                    {!isNaN(params.value) &&
                      params.data.valueType === utilizationLabel &&
                      "%"}

                    {!isNaN(params.value) &&
                      (params.data.valueType === utilizationLabel ||
                        params.data.valueTypeTooltip === "Actual assignment") &&
                      arrowDirection(false)?.icon}

                    {(params.data.valueTypeTooltip ===
                      t("availabilityReportForecast.actualAvailability") ||
                      params.data.valueTypeTooltip ===
                        t(
                          "availabilityReportForecast.theoreticalAvailability"
                        )) && (
                      <span className={`circle ${circleColor()}`}></span>
                    )}
                  </span>
                </Tooltip>
              );
            },
          })),
        ],
      },
    ]);
  }, [rowData]);

  return (
    <>
      {singleAcrissRow.isVisible && (
        <Grid className="forecastTablesContainer">
          <Grid item xs={6}>
            <Box style={gridStyle} className="ag-theme-alpine">
              <AgGridReact
                ref={leftGridRef}
                alignedGrids={
                  rightGridRef.current ? [rightGridRef.current] : undefined
                }
                domLayout="autoHeight"
                rowHeight={42}
                rowData={rowData.leftSide}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                suppressRowHoverHighlight={true}
                className="blueRightBorder"
                tooltipShowDelay={0}
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box style={gridStyle} className="ag-theme-alpine">
              <AgGridReact
                ref={rightGridRef}
                alignedGrids={
                  leftGridRef.current ? [leftGridRef.current] : undefined
                }
                domLayout="autoHeight"
                rowHeight={42}
                rowData={rowData.rightSide}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                suppressRowHoverHighlight={true}
                className="greyBackground"
                tooltipShowDelay={0}
              />
            </Box>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default SingleAcrissTables;
